"use client";

import { useCallback, useMemo, useState } from "react";
import useSWR from "swr";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "sonner";
import { getCurrentLocation, toBounds } from "@/lib/geolocation";
import {
  blankSearchState,
  composeSearch,
  currentCityId,
  draftFromUrl,
  type ComposerDraft,
  type ComposerLocation,
} from "@/lib/search/composer";
import { parseSearchUrl, type SearchUrlState } from "@/lib/search/search-state";
import { liveSearchParams } from "@/lib/search/url-view-sync";
import {
  isSearchPathname,
  navigateToSearch,
} from "@/lib/search/search-navigation";
import { placeDisplayName, type PlaceLocale } from "@/lib/search/place-param";
import { ensureStationPlaces } from "@/lib/search/station-places";
import { cityBySlug, countryByCode } from "@/lib/search/place-slug";
import { placeByRegionCode } from "@/lib/search/place-resolver";
import { getCategoryByCode, getCategoryName } from "@/config/categories";
import { useRecentSearches } from "@/hooks/useRecentSearches";
import { recordSearchEvent } from "@/app/actions/analytics";
import { getSessionId } from "@/lib/analytics/session";
import {
  formatSearchDateLabel,
  hasSearchConditions,
} from "@/lib/analytics/search-meta";

export type ComposerField = "location" | "date" | "what";

/**
 * 검색바 세 칸(위치 | 날짜 | 무엇을)의 초안 상태 + 제출.
 *
 * 초안은 칸을 여는 순간 지금 URL 에서 다시 채운다(open 시 reset) — 검색 페이지에서
 * 필터·지도로 바뀐 조건을 검색바가 뒤늦게 덮어쓰지 않게.
 */
export function useSearchComposer() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const t = useTranslations("searchComposer");
  const tSearch = useTranslations("common.search");
  const { recentSearches, addRecentSearch } = useRecentSearches();

  const onSearchPage = isSearchPathname(pathname);
  const current: SearchUrlState | null = useMemo(
    () => (onSearchPage ? parseSearchUrl(pathname, searchParams) : null),
    [onSearchPage, pathname, searchParams],
  );

  const [draft, setDraft] = useState<ComposerDraft>(() => draftFromUrl(current));
  // 마지막으로 URL 에서 채운 초안 — 열 때 URL 조건이 그대로면 사용자가 고르던 값(날짜 등)을
  // 지우지 않기 위한 기준점(10-06: 날짜만 고르고 닫았다 다시 열면 초기화되던 버그).
  const [urlBaseline, setUrlBaseline] = useState<ComposerDraft>(() => draftFromUrl(current));

  // 검색바는 레이아웃(Navbar)에 있어 페이지를 옮겨도 다시 마운트되지 않는다. 홈의 카테고리
  // 타일·배너로 검색 페이지에 들어오면 URL 만 바뀌므로, URL 이 바뀔 때 초안을 다시 채운다
  // (렌더 중 조정 — effect setState 금지 규칙). 지도가 라우터 없이 쓰는 뷰 값(zoom·clat)은
  // useSearchParams 에 안 잡혀 여기서 반응하지 않는다 — 그건 검색 조건이 아니다.
  const urlKey = `${pathname}?${searchParams.toString()}`;
  const [syncedUrlKey, setSyncedUrlKey] = useState(urlKey);
  if (urlKey !== syncedUrlKey) {
    setSyncedUrlKey(urlKey);
    const fromUrl = draftFromUrl(current);
    setDraft(fromUrl);
    setUrlBaseline(fromUrl);
  }

  // 지금 URL 의 ?place= 가 전 세계 명소(wd:)면 이름은 서버에만 있다 — 한 번 받아 둔다.
  const worldPlaceId = current?.identity.place?.startsWith("wd:") ? current.identity.place : null;
  const { data: worldPlaceName } = useSWR(
    worldPlaceId ? ["world-place", worldPlaceId, locale] : null,
    () =>
      fetch(`/api/places/suggest?id=${encodeURIComponent(worldPlaceId!)}&locale=${locale}`)
        .then((r) => (r.ok ? r.json() : { places: [] }))
        .then((d: { places?: { name: string }[] }) => d.places?.[0]?.name ?? null),
    { revalidateOnFocus: false },
  );
  const [submitting, setSubmitting] = useState(false);

  /**
   * 열 때 호출 — **URL 의 검색 조건이 마지막으로 채운 뒤 바뀌었을 때만** 초안을 다시 채운다.
   * 조건이 그대로면(검색 없이 닫았다 다시 연 경우) 고르던 날짜·위치를 그대로 둔다.
   * 라우터를 거치지 않은 URL 변경(필터·지도)은 liveSearchParams 로 읽어 비교한다.
   */
  const reset = useCallback(() => {
    const live = onSearchPage ? parseSearchUrl(pathname, liveSearchParams(searchParams)) : null;
    const fromUrl = draftFromUrl(live);
    if (JSON.stringify(fromUrl) === JSON.stringify(urlBaseline)) return;
    setDraft(fromUrl);
    setUrlBaseline(fromUrl);
  }, [onSearchPage, pathname, searchParams, urlBaseline]);

  const patch = useCallback((p: Partial<ComposerDraft>) => {
    setDraft((d) => ({ ...d, ...p }));
  }, []);

  const clearAll = useCallback(() => {
    setDraft({
      usage: null,
      location: { kind: "none" },
      checkin: null,
      checkout: null,
      categories: [],
      keyword: "",
    });
  }, []);

  /** 위치 칸 표시값 — 비었으면 null */
  const locationLabel = useCallback(
    (loc: ComposerLocation): string | null => {
      const pl = locale as PlaceLocale;
      switch (loc.kind) {
        case "none":
          return null;
        case "text":
          return loc.text.trim() || null;
        case "nearby":
          return t("nearbyLabel");
        case "place":
          return placeDisplayName(loc.id, pl) ?? loc.point?.name ?? null;
        case "country": {
          const c = countryByCode(loc.code);
          const name = c ? (c.name[pl] ?? c.name.en) : loc.code;
          return t("deliveryCountry", { country: name });
        }
        case "current": {
          const id = current?.identity;
          if (!id) return null;
          if (id.locationSearch === "NEARBY") return t("nearbyLabel");
          if (id.locationSearch === "MAP_AREA") return t("mapArea");
          if (id.place) return placeDisplayName(id.place, pl) ?? worldPlaceName ?? null;
          if (id.scope === "country" && id.country) {
            const c = countryByCode(id.country);
            return c ? (c.name[pl] ?? c.name.en) : null;
          }
          if (id.scope === "region") {
            const pref = placeByRegionCode(id.region);
            return pref ? placeDisplayName(pref.id, pl) : null;
          }
          const city = cityBySlug(id.slug, id.country);
          return city ? (city.name[pl] ?? city.name.en) : null;
        }
      }
    },
    [current, locale, t, worldPlaceName],
  );

  const dateLabel = useMemo(() => {
    if (!draft.checkin) return null;
    const fmt = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" });
    const a = fmt.format(new Date(`${draft.checkin}T00:00:00`));
    if (!draft.checkout || draft.checkout === draft.checkin) return a;
    return `${a} – ${fmt.format(new Date(`${draft.checkout}T00:00:00`))}`;
  }, [draft.checkin, draft.checkout, locale]);

  const whatLabel = useMemo(() => {
    const cat = draft.categories[0] ? getCategoryByCode(draft.categories[0]) : undefined;
    const catName = cat ? getCategoryName(cat, locale as PlaceLocale) : null;
    const parts: string[] = [];
    if (catName) {
      parts.push(
        draft.categories.length > 1
          ? t("categoriesMore", { name: catName, count: draft.categories.length - 1 })
          : catName,
      );
    }
    if (draft.keyword.trim()) parts.push(draft.keyword.trim());
    return parts.length ? parts.join(" · ") : null;
  }, [draft.categories, draft.keyword, locale, t]);

  const submit = useCallback(
    async (override?: Partial<ComposerDraft>): Promise<boolean> => {
      const d: ComposerDraft = { ...draft, ...override };
      // 하루만 고르고 제출 = 당일 대여
      if (d.checkin && !d.checkout) d.checkout = d.checkin;
      const base = onSearchPage
        ? parseSearchUrl(pathname, liveSearchParams(searchParams))
        : blankSearchState();

      let nearbyBounds;
      if (d.location.kind === "nearby") {
        setSubmitting(true);
        const coords = await getCurrentLocation().catch(() => null);
        setSubmitting(false);
        if (!coords) {
          toast.error(tSearch("locationError"));
          return false;
        }
        nearbyBounds = toBounds(coords);
      }

      // 검색어 속 역 이름("강남역")을 지명으로 풀려면 역 사전이 있어야 한다 — 지연 로드라
      // 제출 직전에 기다린다(보통 유휴 시간에 이미 받아 둬 즉시 끝난다).
      await ensureStationPlaces();

      const { url, href } = composeSearch(base, d, {
        locale,
        preferCityId: currentCityId(base),
        nearbyBounds,
      });

      const place = d.location.kind === "current" ? null : locationLabel(d.location);
      const keyword = d.keyword.trim();
      const label = [place, keyword].filter(Boolean).join(" ");
      // 내 주변은 좌표가 매번 달라 최근 검색으로 재현할 가치가 없다.
      if (label && d.location.kind !== "nearby") addRecentSearch(label, href);
      const conditions = {
        checkin: d.checkin,
        checkout: d.checkout,
        categories: d.categories,
        usage: d.usage,
      };
      // 장소·키워드 없이 카테고리/날짜/이용 방식만 고른 검색도 남긴다 — term 은
      // 카테고리명(현재 locale) → 날짜 라벨 → 이용 방식 순으로 채운다.
      const filterTerm = label
        ? null
        : d.categories
            .map((code) => getCategoryByCode(code))
            .filter((cat) => cat !== undefined)
            .map((cat) => getCategoryName(cat, locale as PlaceLocale))
            .join(", ") ||
          formatSearchDateLabel(d.checkin, d.checkout) ||
          d.usage;
      if (label || (filterTerm && hasSearchConditions(conditions))) {
        void recordSearchEvent({
          term: label || filterTerm || "",
          keyword: keyword || null,
          region: place,
          searchType: !label
            ? "filter"
            : place && keyword
              ? "combined"
              : place
                ? "location"
                : "product",
          source: "searchbar",
          sessionId: getSessionId(),
          ...conditions,
        }).catch(() => {});
      }

      navigateToSearch(router, pathname, url);
      return true;
    },
    [draft, onSearchPage, pathname, searchParams, locale, locationLabel, addRecentSearch, router, tSearch],
  );

  /** 최근 검색 칩 — identity 만 담긴 href 로 바로 이동 */
  const openRecent = useCallback(
    (href: string) => navigateToSearch(router, pathname, `/${locale}${href}`),
    [router, pathname, locale],
  );

  return {
    draft,
    setDraft,
    patch,
    reset,
    clearAll,
    submit,
    submitting,
    locationLabel,
    dateLabel,
    whatLabel,
    recentSearches,
    openRecent,
    locale,
  };
}

export type SearchComposerApi = ReturnType<typeof useSearchComposer>;
